<template>
  <transition v-bind="transitionProps">
    <div v-show="isSelected">
      <div v-if="globalAdmin && !hasFeatureFlagEnabled">
        <IconTooltip
          :customPositioning="customTooltipPositionGlobalAdmin"
          :iconClass="sudoIconClassName"
          :tooltipContainerClass="$style.tooltipContainerInItemTable"
        >
          <p>
            The user <strong v-if="user">{{ user.name }}</strong> is configured for Global
            Admin/Sudo privileges. To configure privileges on a system-by-system basis, please
            disable Global Admin/Sudo for this user.
          </p>
          <ul class="list-unstyled">
            <li>
              <i :class="['jc-actions-check', $style.globalAdminTooltipIcon]" />
              System Admin/Sudo
            </li>
            <li v-if="globalAdminPasswordless">
              <i :class="['jc-actions-check', $style.globalAdminTooltipIcon]" />
              Passwordless Sudo
            </li>
          </ul>
        </IconTooltip>
        <span :class="$style.sudoStatusValue">{{ sudoStatusText }}</span>
      </div>
      <div
        v-if="isIndirectlyBound"
        :class="$style.permissionsOptionsContainer"
      >
        <IconTooltip
          :class="$style.permissionIconClassName"
          :customPositioning="customTooltipPositionNonGlobalAdmin"
          :tooltipContainerClass="$style.tooltipContainerInItemTable"
        >
          <p>
            Administrator/Sudo permissions are enabled for this user on this {{ deviceItem.label }}
            through the <b>{{ groupNames }}</b>{{ andMoreText }} user {{ groupCountText }}.
          </p>
        </IconTooltip>
        <div>
          <p :class="$style.permissionsElevatedText">
            Elevated through {{ groupCount }} {{ groupCountText }}
          </p>
          <a
            aria-haspopup="true"
            :class="$style.enablePermissionsLink"
            href="javascript:void(0);"
            @click="handleEnablePermissionsLinkClick()"
          >
            Enable Custom Permissions
          </a>
        </div>
      </div>
      <div v-if="showDropdownComponent">
        <CustomSelectIconList
          :itemType="item.type"
          :listTitle="listTitle"
          :menuItems="menuItems"
          :selectedValueIconClassName="sudoIconClassName"
          :selectedValueText="sudoStatusText"
          :shortColumn="shortColumn"
          :sudoStatusSelectedText="sudoStatusSelectedText"
        />
      </div>
    </div>
  </transition>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import { extend, get } from 'lodash';
import pluralize from 'pluralize';

import { notify } from 'client/util';
import Constants, {
  DirectoryGroupingConsts,
  GroupsConsts,
  ItemTypeConsts,
  ItemTypeDisplayNames,
  StoreModuleMap,
  SudoConsts,
} from 'client/util/Constants';
import CustomSelectIconList from 'components/ItemTable/BodyComponents/CustomSelectIconList.vue';
import DirectoryGroupingApi from 'client/api/v2/DirectoryGroupingApi';
import IconTooltip from 'components/Tooltip/IconTooltip.vue';
import ItemType from 'client/util/Constants/ItemType';
import ModalEventBus from 'components/Modal/ModalEventBus';
import SudoStatusDirectBindingConfirmationModalBody from 'client/components/Modal/SudoStatusDirectBindingConfirmationModalBody.vue';

const { sudoerTypes } = Constants;

const { DeviceItemType } = ItemType;
const deviceItem = new DeviceItemType();

const systemGroupCollection = StoreModuleMap[ItemTypeConsts.systemGroup];

const directBindExists = (paths) => {
  if (paths && paths.length) {
    for (let i = 0; i < paths.length; i += 1) {
      if (paths[i].length === 1) return true;
    }
  }

  return false;
};

const getDirectBindsWithNoPermission = (paths) => {
  const values = [];

  if (paths && paths.length > 1) {
    paths.forEach(array => array.forEach((item, index) => {
      const condition = (
        array.length === 1
        && index === 0
        && !get(item, 'attributes')
      );

      if (condition) values.push(array);
    }));
  }

  return values;
};

const getPathsWithNoSudo = (paths) => {
  const values = [];

  if (paths && paths.length) {
    paths.forEach(array => array.forEach((item) => {
      const condition = array.length > 1 && !get(item, 'to.attributes.sudo');

      if (condition) values.push(array);
    }));
  }

  return values;
};

const getPathsWithSudo = (paths) => {
  const values = [];

  if (paths && paths.length) {
    paths.forEach(array => array.forEach((item) => {
      const condition = get(item, 'to.attributes.sudo');

      if (condition) values.push(array);
    }));
  }

  return values;
};

const getPathsWithSudoGroupAssociations = (paths) => {
  const values = [];

  paths.forEach((path, pathIndex) => path.forEach((node, nodeIndex) => {
    const condition = (
      path.length > 1
        && get(node, 'attributes.sudo', false)
        && get(node, 'to.type', []).includes(ItemTypeConsts.group)
    );

    if (condition) values.push({ ...node, pathIndex, nodeIndex });
  }));

  return values;
};

const mapAndJoin = (responseArray = [], limit = 0, noLimit = false) => {
  if ((responseArray.length > limit) && !noLimit) {
    const namesArray = responseArray.map(group => group.name);
    const filteredNames = namesArray.filter((group, index) => index < limit);

    return filteredNames.join(',');
  }

  if ((responseArray.length < limit) && !noLimit) return responseArray.map(group => group.name).join(',');

  return responseArray.map(group => group.name).join(', ');
};

export default {
  name: 'SudoStatus',

  components: {
    CustomSelectIconList,
    IconTooltip,
  },

  props: {
    customTooltipPositionGlobalAdmin: {
      type: Object,
      default: () => ({ x: 0, y: 0 }),
    },
    customTooltipPositionNonGlobalAdmin: {
      type: Object,
      default: () => ({ x: 0, y: 0 }),
    },
    globalAdmin: {
      required: true,
      type: Boolean,
    },
    globalAdminPasswordless: {
      required: true,
      type: Boolean,
    },
    inheretedAdmin: {
      required: false,
      type: Boolean,
    },
    inheretedAdminPasswordless: {
      required: false,
      type: Boolean,
    },
    item: {
      required: true,
      type: Object,
    },
    parentType: {
      required: true,
      type: String,
      validator: value => value === ItemTypeConsts.device
        || value === ItemTypeConsts.system
        || value === ItemTypeConsts.systemUser
        || value === ItemTypeConsts.systemGroup
        || value === ItemTypeConsts.userGroup,
    },
    showNewWorkflow: {
      type: Boolean,
      default: false,
    },
    storeModule: {
      required: true,
      type: String,
    },
    sudoersContextIsWindows: {
      required: true,
      type: Boolean,
    },
    system: {
      default: null,
      required: false,
      type: Object,
    },
    user: {
      default: null,
      required: false,
      type: Object,
    },
    userGroup: {
      default: null,
      required: false,
      type: Object,
    },
    viewport: {
      default: null,
      required: false,
      type: Object,
    },
  },

  data() {
    const menuItems = [
      {
        onClick: () => this.handleSudoStatusChanged(sudoerTypes.standardUser),
        iconClassName: ['jc-admin-circle', this.$style.sudoIconNone],
        showHeader: false,
        text: 'Standard User',
        type: '',
      },
      {
        onClick: () => this.handleSudoStatusChanged(sudoerTypes.sudo),
        showHeader: false,
        iconClassName: ['jc-admin-circle', this.$style.sudoIconSudoer],
        text: this.sudoersContextIsWindows ? 'Administrator' : 'Sudo',
        type: '',
      },
    ];

    if (!this.sudoersContextIsWindows) {
      menuItems.push({
        onClick: () => this.handleSudoStatusChanged(sudoerTypes.sudoWithoutPassword),
        iconClassName: ['jc-admin-circle', this.$style.sudoIconSudoerWithoutPassword],
        showHeader: false,
        text: 'Sudo without password',
        type: '',
      });
    }

    return {
      deviceItem,
      groupNames: '',
      menuItems,
      sudoStatusSelectedText: '',
      shortColumn: false,
      disableListItem: { isDisabled: true },
    };
  },

  computed: {
    ...mapGetters('SystemUserCollection', ['getDisplayName']),

    andMoreText() {
      return this.groupCount > 5
        ? ', and more'
        : '';
    },

    globalAdminSudoText() {
      return `Global ${SudoConsts.sudo}`;
    },

    groupCount() {
      const { groupTypes } = DirectoryGroupingConsts;
      const userGroups = this.getGroupMembership(this.item.id, groupTypes.userGroup);

      return (userGroups && userGroups.length) || 0;
    },

    groupCountText() {
      return `${pluralize('group', this.groupCount)}`;
    },

    hasFeatureFlagEnabled() {
      return (this.showNewWorkflow
      );
    },

    hasIndirectGlobalPermissions() {
      return (
        !this.globalAdmin
        && this.isItemIndirectlyBound(this.item.id)
        && !this.showPermissionOptions
      );
    },

    isDirectBindWithNoPermissions() {
      const inheritedPermissions = getPathsWithSudo(this.item.paths);
      const isDirectBindWithNoElevatedPermissions = getDirectBindsWithNoPermission(this.item.paths);

      const directBindWithNoPermissions = (
        !this.globalAdmin
        && this.isItemIndirectlyBound(this.item.id, false)
        && inheritedPermissions.length > 0
        && !this.isItemIndirectlyBound(this.item.id)
        && (isDirectBindWithNoElevatedPermissions.length > 0)
      );

      return directBindWithNoPermissions;
    },

    isIndirectlyBound() {
      return (
        !this.globalAdmin
        && !this.hasFeatureFlagEnabled
        && this.isItemIndirectlyBound(this.item.id)
      );
    },

    isSelected() {
      return this.selectedItems.indexOf(this.item.id) !== -1;
    },

    listTitle() {
      const itemType = get(this.item || {}, 'type') || '';
      const isGroupType = itemType.includes(ItemTypeConsts.group);
      let title = '';

      if (this.item && !isGroupType) {
        title = this.item.displayName || this.getDisplayName(this.item);
      } else if (this.item) {
        title = this.item.username;
      }

      return title;
    },

    menuItemType() {
      return get(this.item, 'type', '').includes(ItemTypeConsts.group)
        ? SudoConsts.groupPermissions
        : SudoConsts.individualPermissions;
    },

    pathExists() {
      return get(this.item, 'paths', []).length;
    },

    pathHasGroups() {
      const itemPaths = get(this.item, 'paths', []);

      for (let i = 0; i < itemPaths.length; i += 1) {
        const typeMatch = itemPaths[i].some(item => {
          const itemToType = get(item, 'to.type');

          return (
            itemToType === ItemTypeConsts.userGroup
            || itemToType === ItemTypeConsts.systemGroup
          );
        });

        if (typeMatch) return true;
      }

      return false;
    },

    showDropdownComponent() {
      const showNewWorkflow = this.hasFeatureFlagEnabled;
      const itemIndirectlyBound = this.isItemIndirectlyBound(this.item.id);

      if (showNewWorkflow) {
        return (
          (this.globalAdmin && showNewWorkflow)
          || (!this.globalAdmin && showNewWorkflow && !itemIndirectlyBound)
          || (!this.globalAdmin && showNewWorkflow && itemIndirectlyBound)
          || !this.pathExists()
        );
      }

      return !this.globalAdmin && !itemIndirectlyBound;
    },

    showPermissionOptions() {
      const { id, paths } = this.item;
      const itemIndirectlyBound = this.isItemIndirectlyBound(id);
      const itemDirectlyBound = this.isItemDirectlyBound(id);

      const newBinding = (
        !this.globalAdmin
        && !itemIndirectlyBound
        && this.isSelected
        && (
          this.sudoStatusText === SudoConsts.noPermissions
          || this.sudoStatusText === SudoConsts.elevatePermission
        )
      );

      const directBindWithPermissions = (
        !this.globalAdmin
        && !itemIndirectlyBound
        && this.isSelected
        && !this.pathHasGroups
        && (
          this.sudoStatusText !== SudoConsts.noPermissions
          || this.sudoStatusText !== SudoConsts.elevatePermission
        )
      );

      const verifyIndirectBind = itemDirectlyBound
        ? this.isItemIndirectlyBound(id, false)
        : itemIndirectlyBound;

      const directAndIndirectBinding = (
        !this.globalAdmin
        && verifyIndirectBind
        && (itemDirectlyBound || directBindExists(paths))
        && this.isSelected
      );

      const isGroupToGroupBindOnly = (
        !this.globalAdmin
        && !verifyIndirectBind
        && itemDirectlyBound
        && this.isSelected
        && (paths && paths.length <= 2)
      );

      return (
        newBinding
        || directBindWithPermissions
        || directAndIndirectBinding
        || isGroupToGroupBindOnly
      );
    },

    sudoIconClassName() {
      const styles = [];
      const { showNewWorkflow } = this;

      if (this.globalAdmin) {
        const circleStyle = showNewWorkflow ? 'jc-admin-circle' : 'jc-info-circle';
        let iconStyle = showNewWorkflow
          ? this.$style.sudoIconSudoer
          : this.$style.sudoIconGlobalAdmin;

        iconStyle = this.globalAdminPasswordless && !this.sudoersContextIsWindows
          ? this.$style.sudoIconSudoerWithoutPassword
          : iconStyle;

        styles.push(circleStyle, iconStyle);
      } else if (this.inheretedAdmin || this.inheretedAdminPasswordless) {
        const circleStyle = 'jc-admin-circle';
        const iconStyle = this.inheretedAdmin
          ? this.$style.sudoIconSudoer
          : this.$style.sudoIconSudoerWithoutPassword;

        styles.push(circleStyle, iconStyle);
      } else {
        styles.push('jc-admin-circle');

        switch (this.getSudoerType()) {
          case sudoerTypes.sudo:
            styles.push(this.$style.sudoIconSudoer);

            break;
          case sudoerTypes.sudoWithoutPassword:
            if (this.sudoersContextIsWindows && showNewWorkflow) {
              styles.push(this.$style.sudoIconSudoer);
            }

            if (!this.sudoersContextIsWindows || !showNewWorkflow) {
              styles.push(this.$style.sudoIconSudoerWithoutPassword);
            }

            break;
          case sudoerTypes.noElevatedPermissions:
            styles.push(this.$style.sudoIconNone);

            break;
          default:
            styles.push(this.$style.sudoIconNone);

            break;
        }
      }

      return styles;
    },

    sudoStatusText() {
      let statusText = '';

      const filteredItems = this.menuItems.filter((item) => item.type === GroupsConsts.inheritedGroupPermissions && item.text.startsWith('Global'));

      const { showNewWorkflow } = this;

      const hasIndirectGlobalPermission = this.hasIndirectGlobalPermissions;

      const globalText = hasIndirectGlobalPermission && filteredItems.length > 0 ? 'Global ' : '';

      switch (this.getSudoerType()) {
        case sudoerTypes.sudo:
          if (!showNewWorkflow) {
            statusText = this.sudoersContextIsWindows
              ? 'Administrator'
              : 'Sudo';
          } else {
            statusText = `${globalText}${SudoConsts.sudo}`;
          }

          break;
        case sudoerTypes.sudoWithoutPassword:
          statusText = showNewWorkflow
            ? `${globalText}${SudoConsts.passwordless}`
            : 'Sudo without password';

          if (this.sudoersContextIsWindows && showNewWorkflow) statusText = `${globalText}${SudoConsts.sudo}`;

          break;
        case sudoerTypes.noElevatedPermissions:
          statusText = SudoConsts.noPermissions;

          break;
        default:
          statusText = showNewWorkflow
            ? SudoConsts.noPermissions
            : 'Standard User';

          break;
      }

      const indirectAndDirectBindGlobalPermission = (
        !hasIndirectGlobalPermission
        && filteredItems.length > 0
        && this.sudoStatusSelectedText === SudoConsts.noPermissions
        && statusText !== SudoConsts.noPermissions
      );

      if (this.inheretedAdmin) statusText = SudoConsts.sudo;
      if (this.inheretedAdminPasswordless) statusText = SudoConsts.passwordless;
      if (this.globalAdmin) {
        statusText = showNewWorkflow
          ? this.globalAdminSudoText
          : 'Global Admin/Sudo';
      }
      if (this.globalAdminPasswordless && showNewWorkflow) {
        statusText = this.sudoersContextIsWindows
          ? this.globalAdminSudoText
          : 'Global Passwordless Sudo';
      }

      if (indirectAndDirectBindGlobalPermission) statusText = `Global ${statusText}`;

      return statusText;
    },

    transitionProps() {
      return {
        enterClass: this.$style.containerEnter,
        enterActiveClass: this.$style.containerTransition,
        leaveActiveClass: this.$style.containerTransition,
        leaveToClass: this.$style.containerLeaveTo,
      };
    },
  },

  watch: {
    async isSelected(newValue, oldValue) {
      const wasItemNotSelected = (
        !this.globalAdmin
        && !this.isItemIndirectlyBound(this.item.id)
        && !oldValue
        && (
          this.sudoStatusText !== SudoConsts.noPermissions
          || this.sudoStatusText !== SudoConsts.elevatePermission
        )
      );

      const isItemDirectlyBound = (
        !this.globalAdmin
        && !this.isItemIndirectlyBound(this.item.id)
        && newValue
        && (
          this.sudoStatusText !== SudoConsts.noPermissions
          || this.sudoStatusText !== SudoConsts.elevatePermission
        )
      );

      if (wasItemNotSelected && isItemDirectlyBound) {
        this.menuItems = await this.getMenuItems();
      }
    },
  },

  /**
   * Extend the computed properties and methods here, before the component is
   * created so that the namespace argument to mapActions() and mapGetters()
   * can be set dynamically from the props passed to the SudoStatus component.
   * @see https://github.com/vuejs/vuex/issues/863#issuecomment-315619012
   */
  beforeCreate() {
    extend(
      this.$options.computed,
      mapGetters(this.$options.propsData.storeModule, {
        getPathInfoForTarget: 'getPathInfoForTarget',
        getSudoer: 'getSudoer',
        getGroupMembership: 'getGroupMembership',
        isItemDirectlyBound: 'isItemDirectlyBound',
        isItemIndirectlyBound: 'isItemIndirectlyBound',
        isItemPreviouslyDirectlyBound: 'isItemPreviouslyDirectlyBound',
        selectedItems: 'getSelectedItems',
      }),

    );

    extend(
      this.$options.methods,
      mapActions(this.$options.propsData.storeModule, [
        'restorePreviousDirectBinding',
        'setSudoer',
        'createDirectBinding',
      ]),
      mapActions('SystemGroupModel', { getSystemGroupAssociations: 'getSystemGroupAssociations' }),
    );
  },

  mounted() {
    this.handleMounted();
  },

  methods: {
    ...mapActions(systemGroupCollection, {
      fetchSystemGroupById: 'fetchSystemGroupById',
    }),

    displayAddDirectBindingModal(userGroups) {
      return new Promise((resolve) => {
        const componentProps = {
          deviceItemLabel: deviceItem.label,
          handleCancel: () => {
            ModalEventBus.close();
            resolve({ shouldContinue: false });
          },
          handleContinue: () => {
            ModalEventBus.close();
            resolve({ shouldContinue: true });
          },
          showClose: true,
          systemName: this.system.name,
          userGroupNames: mapAndJoin(userGroups, 0, true),
          userName: this.user.name,
        };

        ModalEventBus.open({
          isLargeModal: false,
          component: SudoStatusDirectBindingConfirmationModalBody,
          componentProps,
        });
      });
    },

    getAssociatedUserGroupName(path) {
      const userGroupPath = get(this.item, `paths[${path.pathIndex}]`);
      if (!userGroupPath) return '';

      const userGroupIndex = userGroupPath.findIndex(p => get(p, 'to.type') === ItemTypeConsts.userGroup);

      return userGroupIndex > -1
        ? get(userGroupPath, `[${userGroupIndex}].to.attributes.ldapGroups[0].name`, '')
        : '';
    },

    async getDeviceGroupAssociations() {
      const parentId = this.item.id;
      const targetType = ItemTypeConsts.userGroup;
      const associations = await this.getSystemGroupAssociations(
        { parentId, targetType },
      );

      return associations;
    },

    async getDeviceGroupNameFromPath(path) {
      let deviceGroupId;
      let deviceGroupIndex;

      switch (path.to.type) {
        case ItemTypeConsts.userGroup:
          deviceGroupIndex = this.item.paths[path.pathIndex]
            .findIndex(p => p.to.type === ItemTypeConsts.systemGroup);

          deviceGroupId = this.item.paths[path.pathIndex][deviceGroupIndex].to.id;

          break;
        case ItemTypeConsts.systemGroup:
          deviceGroupId = path.to.id;

          break;
        default:
          break;
      }

      if (!deviceGroupId) return '';

      const deviceGroup = await this.fetchSystemGroupById({ id: deviceGroupId });
      return deviceGroup.name;
    },

    async getGroupNames() {
      const limit = 5;
      const { groupTypes } = DirectoryGroupingConsts;
      const ids = this.getGroupMembership(this.item.id, groupTypes.userGroup);
      let userGroupNames = '';

      if (ids && ids.length) {
        const response = await DirectoryGroupingApi.getGroupNames(ItemTypeConsts.userGroup, ids);

        if (response && response.length) {
          userGroupNames = mapAndJoin(response, limit, false);
        }
      }

      return userGroupNames;
    },

    async getMenuItems() {
      const { menuItemType } = this;
      const menuItemsFromPaths = await this.getPermissionsFromPaths();
      let menuItems = [];

      if (this.hasFeatureFlagEnabled) {
        menuItems = (this.globalAdmin || !this.showPermissionOptions) ? [] : [
          {
            onClick: () => this.handleSudoStatusChanged(sudoerTypes.sudo, SudoConsts.sudo),
            iconClassName: ['jc-admin-circle', this.$style.sudoIconSudoer],
            showHeader: true,
            text: SudoConsts.sudo,
            type: menuItemType,
          },
        ];

        const isPasswordlessVisible = (
          !this.sudoersContextIsWindows
          && this.showPermissionOptions
          && !this.globalAdmin
          && get(menuItems, '[0].text') === SudoConsts.sudo
        );

        if (isPasswordlessVisible) {
          menuItems.push({
            onClick: () => this.handleSudoStatusChanged(sudoerTypes.sudoWithoutPassword, 'Passwordless Sudo'),
            iconClassName: ['jc-admin-circle', this.$style.sudoIconSudoerWithoutPassword],
            showHeader: false,
            text: SudoConsts.passwordless,
            type: menuItemType,
          });
        }

        if (this.showPermissionOptions && !this.globalAdmin) {
          menuItems.push({
            onClick: () => this.handleSudoStatusChanged(
              sudoerTypes.noElevatedPermissions,
              SudoConsts.noPermissions,
            ),
            disabled: false,
            iconClassName: ['jc-close-light', this.$style.sudoIconNoElevatedPermissions],
            selected: true,
            showHeader: false,
            text: SudoConsts.noPermissions,
            type: menuItemType,
          });
        }

        menuItems = [...menuItems, ...menuItemsFromPaths];
      } else {
        menuItems = [
          {
            onClick: () => this.handleSudoStatusChanged(sudoerTypes.standardUser),
            iconClassName: ['jc-admin-circle', this.$style.sudoIconNone],
            showHeader: false,
            text: 'Standard User',
            type: '',
          },
          {
            onClick: () => this.handleSudoStatusChanged(sudoerTypes.sudo),
            iconClassName: ['jc-admin-circle', this.$style.sudoIconSudoer],
            showHeader: false,
            text: this.sudoersContextIsWindows ? 'Administrator' : 'Sudo',
            type: '',
          },
        ];

        if (!this.sudoersContextIsWindows) {
          menuItems.push({
            onClick: () => this.handleSudoStatusChanged(sudoerTypes.sudoWithoutPassword),
            iconClassName: ['jc-admin-circle', this.$style.sudoIconSudoerWithoutPassword],
            showHeader: false,
            text: 'Sudo without password',
            type: '',
          });
        }
      }

      return menuItems;
    },

    async getPermissionsFromPaths() {
      const hasGlobalPermissions = this.globalAdmin || this.globalAdminPasswordless;
      let permissionsItems = [];

      if (hasGlobalPermissions && !this.parentType.includes(ItemTypeConsts.group)) {
        const globalAdminPermissions = this.pushPermissionsWithGlobalAdmin();
        permissionsItems = [...globalAdminPermissions];
      }

      if (this.item && this.item.paths) {
        const paths = getPathsWithSudo(this.item.paths);
        const pathsWithGroupAssociations = getPathsWithSudoGroupAssociations(this.item.paths);

        const sudoPaths = this.pushPermissionsWithSudo(paths).reduce((list, current) => {
          if (!list.some(item => item.text === current.text)) list.push(current);

          return list;
        }, []);

        const sudoPathsWithAssociations = await this.pushPermissionsWithSudoGroupAssociations(
          pathsWithGroupAssociations,
          sudoPaths.length,
        );

        permissionsItems = [...permissionsItems, ...sudoPaths, ...sudoPathsWithAssociations];
      }

      if (this.item.type === ItemTypeConsts.systemGroup) {
        const deviceGroupsPermissions = await this.pushPermissionsWithSudoDeviceGroups();
        permissionsItems = [...permissionsItems, ...deviceGroupsPermissions];
      }

      const noSudoPaths = getPathsWithNoSudo(this.item.paths);
      const hasIndirectBindsNoPermissions = noSudoPaths.length && !permissionsItems.length;

      const hasIndirectBindsWithGlobalAdmin = (
        noSudoPaths.length
        && permissionsItems.length === 1
        && hasGlobalPermissions
      );

      if (hasIndirectBindsNoPermissions) {
        const permission = {
          onClick: () => {},
          iconClassName: '',
          showHeader: true,
          text: "This user isn't bound to any groups with elevated permissions.",
          type: GroupsConsts.inheritedGroupPermissions,
        };

        permissionsItems.push(permission);
      }

      if (!this.pathHasGroups || hasIndirectBindsWithGlobalAdmin) {
        const permission = {
          onClick: () => {},
          iconClassName: '',
          showHeader: true,
          text: "This user isn't bound to any groups.",
          type: GroupsConsts.inheritedGroupPermissions,
          ...this.disableListItem,
        };

        permissionsItems.push(permission);
      }

      return permissionsItems;
    },

    getSudoerType() {
      const sudoer = this.getSudoer(this.item.id);
      const isFeatureFlagEnabled = this.hasFeatureFlagEnabled;

      if (!sudoer || (sudoer && !sudoer.enabled)) {
        return isFeatureFlagEnabled
          ? sudoerTypes.noElevatedPermissions
          : sudoerTypes.standardUser;
      }

      return sudoer.withoutPassword
        ? sudoerTypes.sudoWithoutPassword
        : sudoerTypes.sudo;
    },

    handleEnablePermissionsLinkClick() {
      const { groupTypes } = DirectoryGroupingConsts;

      // 1) If the item was previously directly bound then just restore the
      // previous direct binding. It hasn't been removed from the server yet!
      if (this.isItemPreviouslyDirectlyBound(this.item.id)) {
        this.restorePreviousDirectBinding(this.item.id);

        return;
      }

      // 2) Get the IDs of the user groups that indirectly bind the system to
      // the user.
      const userGroupIds = this.getGroupMembership(this.item.id, groupTypes.userGroup);

      // 3) Make an XHR request to get the target group names given the target
      // group IDs that the target is a member of.
      DirectoryGroupingApi
        .getGroupNames(ItemTypeConsts.userGroup, userGroupIds)
        .then((userGroups) => {
          // 4) Prompt admin to confirm if they want to remove the user from
          // all groups that provide access to the system
          this
            .displayAddDirectBindingModal(userGroups)
            .then(({ shouldContinue }) => {
              if (!shouldContinue) {
                return;
              }

              let parentId;
              let parentType;
              let targetId;
              let targetType;

              if (
                this.parentType === ItemTypeConsts.system
                || this.parentType === ItemTypeConsts.device
              ) {
                parentId = this.system.id;
                parentType = ItemTypeConsts.system;
                targetId = this.user.id;
                targetType = ItemTypeConsts.systemUser;
              } else {
                parentId = this.user.id;
                parentType = ItemTypeConsts.systemUser;
                targetId = this.system.id;
                targetType = ItemTypeConsts.system;
              }

              // 5) If the admin clicks Continue then create a direct binding
              // from the system to th euser.
              this
                .createDirectBinding({
                  parentType,
                  parentId,
                  targetType,
                  targetId,
                })
                .catch(() => {
                  notify('error', 'Unable to create direct binding!');
                });
            });
        })
        .catch(() => {
          notify('error', 'Unable to lookup target group names!');
        });
    },

    async handleMounted() {
      if (this.item.type === ItemTypeConsts.systemGroup) {
        this.shortColumn = true;
      }

      this.groupNames = await this.getGroupNames();
      this.menuItemsFromPaths = await this.getPermissionsFromPaths();
      this.menuItems = await this.getMenuItems();

      if (!this.sudoStatusSelectedText) {
        this.sudoStatusSelectedText = this.isDirectBindWithNoPermissions
          ? SudoConsts.noPermissions
          : this.sudoStatusText;
      }
    },

    handleSudoStatusChanged(value, selectedText) {
      const payload = {
        id: this.item.id,
        enabled: false,
        withoutPassword: false,
      };

      switch (value) {
        case sudoerTypes.sudo:
          payload.enabled = true;

          break;
        case sudoerTypes.sudoWithoutPassword:
          payload.enabled = true;
          payload.withoutPassword = true;

          break;
        case sudoerTypes.noElevatedPermissions:
          delete payload.enabled;
          delete payload.withoutPassword;

          break;
        case sudoerTypes.standardUser:
        default:
          delete payload.enabled;
          delete payload.withoutPassword;

          break;
      }

      this.setSudoer(payload);
      if (selectedText) this.sudoStatusSelectedText = selectedText;
    },

    pushPermissionsWithGlobalAdmin() {
      const permissionsItems = [];
      const permissionLabelText = (this.globalAdminPasswordless && !this.sudoersContextIsWindows)
        ? 'Global Passwordless Sudo'
        : this.globalAdminSudoText;

      const permissionText = `${permissionLabelText} on all devices`;

      const sudoType = permissionLabelText === this.globalAdminSudoText
        ? sudoerTypes.sudo
        : sudoerTypes.sudoWithoutPassword;

      const sudoIcon = permissionLabelText === this.globalAdminSudoText
        ? this.$style.sudoIconSudoer
        : this.$style.sudoIconSudoerWithoutPassword;

      const permission = {
        onClick: () => this.handleSudoStatusChanged(sudoType),
        iconClassName: ['jc-admin-circle', sudoIcon],
        showHeader: true,
        text: permissionText,
        type: this.menuItemType,
        ...this.disableListItem,
      };

      permissionsItems.push(permission);
      return permissionsItems;
    },

    pushPermissionsWithSudo(paths) {
      const permissionsItems = [];
      let typeText = '';

      paths.forEach((array, index) => array.forEach(item => {
        const { attributes, type } = item.to;

        if (attributes && attributes.sudo) {
          const { name } = attributes.ldapGroups[0];
          const permissionLabelText = (
            (attributes.sudo.enabled && !attributes.sudo.withoutPassword)
            || this.sudoersContextIsWindows
          ) ? SudoConsts.sudo : SudoConsts.passwordless;

          switch (type) {
            case ItemTypeConsts.userGroup:
              typeText = ItemTypeDisplayNames[ItemTypeConsts.userGroup];

              break;
            case ItemTypeConsts.systemGroup:
              typeText = ItemTypeDisplayNames[ItemTypeConsts.deviceGroup];

              break;
            default:
              typeText = '';

              break;
          }

          const globalText = type === ItemTypeConsts.userGroup ? 'Global ' : '';
          const permissionText = `${globalText}${permissionLabelText} on <b>${typeText}: ${name}</b>`;

          const sudoType = permissionLabelText === SudoConsts.sudo
            ? sudoerTypes.sudo
            : sudoerTypes.sudoWithoutPassword;

          const sudoIcon = permissionLabelText === SudoConsts.sudo
            ? this.$style.sudoIconSudoer
            : this.$style.sudoIconSudoerWithoutPassword;

          const permission = {
            onClick: () => this.handleSudoStatusChanged(sudoType),
            iconClassName: ['jc-admin-circle', sudoIcon],
            showHeader: index === 0,
            styledText: {
              boldText: `${typeText}: ${name}`,
              isStyledText: true,
              noBoldText: `${globalText}${permissionLabelText} on `,
            },
            text: permissionText,
            type: GroupsConsts.inheritedGroupPermissions,
            ...this.disableListItem,
          };

          permissionsItems.push(permission);
        }
      }));

      return permissionsItems;
    },

    async pushPermissionsWithSudoDeviceGroups() {
      const associations = await this.getDeviceGroupAssociations();
      const typeText = ItemTypeDisplayNames[ItemTypeConsts.userGroup];
      const globalText = this.globalAdmin || this.globalAdminPasswordless ? 'Global ' : '';

      // HY-1887: in User Group "x" aside -> Device groups tab, only show
      // permissions associated with User Group "x"
      return associations
        .filter(item => {
          const { attributes, type, id } = item.to;

          // keep: only device group associations related to user group,
          // if those associations have sudo, and if association is a user group
          return (
            id === this.userGroup.id
            && Object.keys(get(attributes, 'sudo', {})).length
            && type === ItemTypeConsts.userGroup
          );
        })
        .reduce((permissionsItems, item, index) => {
          const { attributes } = item.to;
          const permissionLabelText = attributes.sudo.enabled && !attributes.sudo.withoutPassword
            ? SudoConsts.sudo
            : SudoConsts.passwordless;

          const { name } = attributes.ldapGroups[0];
          const permissionText = `${globalText}${permissionLabelText} on <b>${typeText}: ${name}</b>`;

          const sudoType = permissionLabelText === SudoConsts.sudo
            ? sudoerTypes.sudo
            : sudoerTypes.sudoWithoutPassword;

          const sudoIcon = permissionLabelText === SudoConsts.sudo
            ? this.$style.sudoIconSudoer
            : this.$style.sudoIconSudoerWithoutPassword;

          const permission = {
            onClick: () => this.handleSudoStatusChanged(sudoType),
            iconClassName: ['jc-admin-circle', sudoIcon],
            showHeader: index === 0 || (!permissionsItems.length && !!index),
            styledText: {
              boldText: `${typeText}: ${name}`,
              isStyledText: true,
              noBoldText: `${globalText}${permissionLabelText} on `,
            },
            text: permissionText,
            type: GroupsConsts.inheritedGroupPermissions,
            ...this.disableListItem,
          };

          permissionsItems.push(permission);
          return permissionsItems;
        }, []);
    },

    async pushPermissionsWithSudoGroupAssociations(paths, hasPermissions = false) {
      const permissionsItems = await paths.map(async (path, index) => {
        let typeText = '';
        let associationText = '';
        let toBindName = '';
        const { attributes } = path;
        const { type } = path.to;

        const permissionLabelText = (
          (attributes.sudo.enabled && !attributes.sudo.withoutPassword)
          || this.sudoersContextIsWindows
        ) ? SudoConsts.sudo : SudoConsts.passwordless;

        switch (type) {
          case ItemTypeConsts.userGroup:
            typeText = ItemTypeDisplayNames[ItemTypeConsts.userGroup];
            associationText = ItemTypeDisplayNames[ItemTypeConsts.deviceGroup];
            toBindName = await this.getDeviceGroupNameFromPath(path);

            break;
          case ItemTypeConsts.systemGroup:
            typeText = ItemTypeDisplayNames[ItemTypeConsts.deviceGroup];
            associationText = ItemTypeDisplayNames[ItemTypeConsts.userGroup];
            toBindName = this.getAssociatedUserGroupName(path);

            break;
          default:
            typeText = '';

            break;
        }

        const fromBindName = type === ItemTypeConsts.userGroup
          ? path.to.attributes.ldapGroups[0].name
          : await this.getDeviceGroupNameFromPath(path);

        const permissionText = `${permissionLabelText} on bind of <b>${typeText}: ${fromBindName} to ${associationText}: ${toBindName}</b>`;

        const sudoType = permissionLabelText === SudoConsts.sudo
          ? sudoerTypes.sudo
          : sudoerTypes.sudoWithoutPassword;

        const sudoIcon = permissionLabelText === SudoConsts.sudo
          ? this.$style.sudoIconSudoer
          : this.$style.sudoIconSudoerWithoutPassword;

        return {
          onClick: () => this.handleSudoStatusChanged(sudoType),
          iconClassName: ['jc-admin-circle', sudoIcon],
          showHeader: !hasPermissions && index === 0,
          styledText: {
            boldText: `${typeText}: ${fromBindName} to ${associationText}: ${toBindName}`,
            isStyledText: true,
            noBoldText: `${permissionLabelText} on bind of `,
          },
          text: permissionText,
          type: GroupsConsts.inheritedGroupPermissions,
          ...this.disableListItem,
        };
      });

      const permissions = await Promise.all(permissionsItems);
      return permissions;
    },
  },
};
</script>

<style module>
@import '../../../css/tooltips.css';

@value (panelSpeed, panelTimingFunction) from '~css/transitions.css';
@value (
  gradientDark3,
  infoBlue,
  jcGreen,
  jcOrange,
  lighterNeutralDark10,
  neutralDark
) from '~css/colors.css';

.containerEnter,
.containerLeaveTo {
  opacity: 0;
}

.containerTransition {
  transition: opacity panelSpeed panelTimingFunction;
}

.enablePermissionsLink[aria-haspopup="true"] {
  color: neutralDark;
  font-size: 0.9em;
  line-height: 1.9rem;
  text-decoration: underline;
}

.permissionIconClassName {
  margin-bottom: 1.8rem;
  margin-right: 0.4rem;
}

.permissionsElevatedText {
  margin: 0;
}

.permissionsOptionsContainer {
  align-items: center;
  display: inline-flex;
  flex-direction: row;
}

.sudoIconGlobalAdmin {
  color: infoBlue;
}

.sudoIconNoElevatedPermissions {
  color: gradientDark3;
}

.sudoIconSudoer {
  color: jcGreen;
}

.sudoIconSudoerWithoutPassword {
  color: jcOrange;
}

.sudoIconNone {
  display: none;
}

.sudoStatusValue {
  color: lighterNeutralDark10;
  font-size: 12px;
  font-weight: 400;
  margin-right: 8px;
}

div.test:hover span {
  display: block;
}

.globalAdminTooltipIcon {
  color: jcGreen;
  display: inline-block;
  font-size: 16px;
  margin-bottom: 3px;
  margin-right: 8px;
  vertical-align: middle;
}
</style>
